import {EXTENSIONS as SUPPORTED_EXTENSIONS} from '@apps-common/importer';
import {UploadFile as UploadFileIcon} from '@mui/icons-material';
import {Avatar, Box, Button, Link, Typography, lighten} from '@mui/material';
import numeral from 'numeral';
import {array, bool, func, node, number, object} from 'prop-types';
import React from 'react';
import ReactDropzone from 'react-dropzone';
import {trackEvent} from '@app/analytics';
import {MAX_ASYNC_UPLOADS_SIZE_BYTES} from '@app/config';

//ToDo: move to importer to centralize next to EXTENSIONS
// See https://react-dropzone.js.org/#section-accepting-specific-file-types
const SUPPORTED_FILE_TYPES = {
  'application/json': ['.geojson', '.json'],
  'application/octet-stream': ['.shp', '.shx', '.gpx'],
  'application/x-dbf': ['.dbf'],
  'application/text': ['.prj', '.wkt'],
  'application/vnd.google-earth.kml+xml': ['.kml'],
  'application/vnd.google-earth.kmz': ['.kmz'],
  'application/zip': ['.zip'],
};

const DEFAULT_EXTENSIONS = SUPPORTED_EXTENSIONS;

export const DropZone = ({
  files = [],
  children,
  onDrop,
  supportedExtensions = DEFAULT_EXTENSIONS,
  acceptedContentTypes = SUPPORTED_FILE_TYPES,
  allowAdd = true,
  maxSize = MAX_ASYNC_UPLOADS_SIZE_BYTES,
  sx,
  ...props
}) => {
  return (
    <ReactDropzone
      noClick
      onDrop={onDrop}
      accept={acceptedContentTypes}
      maxSize={maxSize}
      {...props}
    >
      {({getRootProps, getInputProps, isDragActive, open}) => (
        <>
          <Box
            {...getRootProps()}
            sx={[
              {
                margin: 2,
                borderWidth: files.length > 0 ? 0 : 2,
                borderStyle: 'dashed',
                borderRadius: 1,
                borderColor: isDragActive ? 'primary.main' : 'text.secondary',
                backgroundColor: theme =>
                  files.length > 0 && !isDragActive
                    ? 'paper'
                    : isDragActive
                      ? `rgba(${(theme.vars || theme).palette.primary.mainChannel} / (${(theme.vars || theme).palette.action.hoverOpacity} +
                            ${(theme.vars || theme).palette.action.activatedOpacity}))`
                      : `rgba(${(theme.vars || theme).palette.primary.mainChannel} / ${(theme.vars || theme).palette.action.activatedOpacity})`,
                outline: 'none',
                cursor: 'pointer',
                transition: 'color .24s ease-in-out, border .24s ease-in-out',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: files.length > 0 ? 'flex-start' : 'center',
                flex: '1 1 auto',
                position: 'relative',
              },
              ...(Array.isArray(sx) ? sx : [sx]),
            ]}
            data-testid="dropzone"
          >
            <input {...getInputProps()} data-testid="drop-input" />
            {files.length === 0 && (
              <>
                <Avatar
                  sx={theme => ({
                    backgroundColor: lighten(theme.palette.primary.light, 0.75),
                    width: 56,
                    height: 56,
                    marginBottom: 2,
                    color: 'primary.main',
                    ...theme.applyStyles('dark', {
                      color: 'primary.dark',
                    }),
                  })}
                >
                  <UploadFileIcon fontSize="large" />
                </Avatar>
                <Typography
                  variant="h6"
                  sx={{
                    marginBottom: 1,
                    textAlign: 'center',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <Link
                    component="button"
                    variant="inherit"
                    color="primary"
                    onClick={open}
                  >
                    Click to upload
                  </Link>{' '}
                  or drag and drop
                </Typography>
                <Typography
                  align="center"
                  sx={{marginBottom: 1, color: 'text.secondary'}}
                >
                  Supported file types: {supportedExtensions.join(', ')}
                </Typography>
                <Typography align="center" sx={{color: 'text.secondary'}}>
                  Maximum file size: {numeral(maxSize).format('0b')}
                </Typography>
              </>
            )}
            {files.length > 0 && (
              <>
                {children}
                {allowAdd && (
                  <>
                    <Box
                      sx={{
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'flex-start',
                      }}
                    >
                      <Button
                        variant="contained"
                        onClick={() => {
                          trackEvent('Async Upload', {
                            action: 'Clicked on add more files',
                          });
                          open();
                        }}
                      >
                        Add more files
                      </Button>
                    </Box>
                  </>
                )}
              </>
            )}
          </Box>
        </>
      )}
    </ReactDropzone>
  );
};

DropZone.propTypes = {
  files: array,
  children: node,
  onDrop: func,
  supportedExtensions: array,
  acceptedContentTypes: object,
  allowAdd: bool,
  maxSize: number,
  sx: object,
};

export default DropZone;
